import {
  EDITABLE_ITC_STATUSES,
  LABELS,
  marketingExpenseCreateSchema,
  marketingExpenseUpdateSchema,
  yesterdayInTimezone,
} from '@mm/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError, type QueryParams } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import { ExportButton } from '../../components/ExportButton';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Button } from '../../components/ui/Button';
import { Badge, Card, PageHeader } from '../../components/ui/display';
import { Input } from '../../components/ui/inputs';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useToast } from '../../components/ui/Toast';
import { type FieldDef, type FormValues, ResourceForm } from '../../forms/ResourceForm';
import { useDebounce } from '../../hooks/useDebounce';
import { useLookups } from '../../hooks/useLookups';
import { DataTable } from '../../tables/DataTable';
import type { MarketingExpense, MarketingSummary } from '../../types/models';
import { formatDate, formatMoney, formatPercent } from '../../utils/format';
import { itcBuckets } from '../../utils/itc';
import { useAuth } from '../auth/AuthProvider';
import { enumOptions } from '../masters/configs';
import { sources } from '../masters/sources';

const ITC_TONES = {
  ELIGIBLE: 'green',
  PENDING_VERIFICATION: 'amber',
  CLAIMED: 'violet',
  NOT_ELIGIBLE: 'slate',
  REVERSED: 'rose',
} as const;

export function ItcBadge({ status }: { status: keyof typeof ITC_TONES }) {
  return <Badge tone={ITC_TONES[status]}>{LABELS.itcStatus[status]}</Badge>;
}

const invoiceFields: FieldDef[] = [
  { name: 'invoiceNumber', label: 'Invoice number', type: 'text' },
  { name: 'invoiceDate', label: 'Invoice date', type: 'date' },
  { name: 'vendorName', label: 'Vendor', type: 'text', placeholder: 'Google India Pvt Ltd' },
  { name: 'vendorGstin', label: 'Vendor GSTIN', type: 'text' },
  {
    name: 'itcStatus',
    label: 'ITC status',
    type: 'select',
    required: true,
    options: enumOptions(EDITABLE_ITC_STATUSES, LABELS.itcStatus),
    help: 'New marketing GST is eligible input credit automatically',
  },
  { name: 'notes', label: 'Notes', type: 'textarea', wide: true },
];

const createFields: FieldDef[] = [
  { name: 'transactionDate', label: 'Date', type: 'date', required: true },
  { name: 'appId', label: 'App', type: 'select', required: true, remote: sources.app() },
  {
    name: 'advertisingAccountId',
    label: 'Ads account',
    type: 'select',
    required: true,
    help: 'Accounts currently assigned to the app',
    remote: (v) =>
      sources.advertisingAccount(
        v.appId ? { status: 'ACTIVE', appId: String(v.appId) } : { status: 'ACTIVE' },
      ),
  },
  {
    name: 'expenseBeforeGst',
    label: 'Spend before GST (₹)',
    type: 'decimal',
    required: true,
    help: 'GST is added at the default rate (Settings → Marketing)',
  },
  ...invoiceFields,
];

const editFields: FieldDef[] = [
  { name: 'expenseBeforeGst', label: 'Spend before GST', type: 'decimal', required: true },
  {
    name: 'gstAmount',
    label: 'GST amount (override)',
    type: 'decimal',
    wide: true,
    help: 'Leave as calculated, or enter the exact GST from the invoice (flagged as overridden)',
  },
  ...invoiceFields,
];

const str = (v: unknown) => (v == null ? '' : String(v));

type Editing = { mode: 'create' } | { mode: 'edit'; row: MarketingExpense } | null;

export function MarketingPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: lookups } = useLookups();
  const [range, setRange] = useDateFilter('thisMonth');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('-transactionDate');
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<MarketingExpense | null>(null);
  const debounced = useDebounce(search);
  const canWrite = can('transactions.write');

  const filterParams: QueryParams = {
    startDate: range.startDate,
    endDate: range.endDate,
    ...filters,
  };
  const list = useQuery({
    queryKey: ['marketing', 'list', filterParams, page, sort, debounced],
    queryFn: () =>
      api.list<MarketingExpense>('/marketing-expenses', {
        ...filterParams,
        page,
        pageSize: 50,
        sort,
        search: debounced,
      }),
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: ['marketing', 'summary', filterParams],
    queryFn: () => api.get<MarketingSummary>('/marketing-expenses/summary', filterParams),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['marketing'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['morning-entry'] });
  };

  const remove = useMutation({
    mutationFn: (row: MarketingExpense) => api.delete(`/marketing-expenses/${row.id}`),
    onSuccess: () => {
      toast.success('Entry deleted');
      setDeleting(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed');
      setDeleting(null);
    },
  });

  const setFilter = (name: string, value: string) => {
    setFilters((f) => ({ ...f, [name]: value }));
    setPage(1);
  };

  const s = summary.data;
  const editRow = editing?.mode === 'edit' ? editing.row : null;

  return (
    <>
      <PageHeader
        title="Marketing"
        description="Daily marketing spend per advertising account. Amounts before GST are the marketing expense; GST is tracked separately as input credit."
        actions={
          <>
            <ExportButton
              report="marketing-expenses"
              params={{ ...filterParams, search: debounced }}
            />
            <DateRangeFilter
              value={range}
              onChange={(v) => {
                setRange(v);
                setPage(1);
              }}
            />
            {canWrite && (
              <Button
                icon={<Plus className="size-4" />}
                onClick={() => setEditing({ mode: 'create' })}
              >
                Add entry
              </Button>
            )}
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Marketing (before GST)" value={formatMoney(s?.expenseBeforeGst)} />
        <Stat label="GST paid" value={formatMoney(s?.gstAmount)} tone="gst" />
        <Stat label="Total (cash outflow)" value={formatMoney(s?.totalAmount)} />
        <Stat
          label="GST ITC remaining"
          value={s ? formatMoney(itcBuckets(s.gstAmount, s.itcByStatus).remaining) : '—'}
          tone="gst"
        />
        <Stat label="Entries" value={s ? String(s.count) : '—'} />
      </div>

      <Card bodyClassName="p-0">
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search account, invoice, vendor…"
              className="pl-9"
              aria-label="Search"
            />
          </div>
          <SearchableSelect
            ariaLabel="App"
            placeholder="All apps"
            clearable
            value={filters.appId ?? ''}
            remote={sources.app({})}
            onChange={(v) => setFilter('appId', v)}
          />
          <SearchableSelect
            ariaLabel="Platform"
            placeholder="All platforms"
            clearable
            value={filters.advertisingPlatformId ?? ''}
            remote={sources.platform({})}
            onChange={(v) => setFilter('advertisingPlatformId', v)}
          />
          <SearchableSelect
            ariaLabel="Advertising account"
            placeholder="All accounts"
            clearable
            value={filters.advertisingAccountId ?? ''}
            remote={sources.advertisingAccount({})}
            onChange={(v) => setFilter('advertisingAccountId', v)}
          />
          <SearchableSelect
            ariaLabel="GST account"
            placeholder="All GST accounts"
            clearable
            value={filters.gstAccountId ?? ''}
            remote={sources.gstAccount({})}
            onChange={(v) => setFilter('gstAccountId', v)}
          />
          <SearchableSelect
            ariaLabel="MCC"
            placeholder="All MCCs"
            clearable
            value={filters.managerAccountId ?? ''}
            remote={sources.managerAccount({})}
            onChange={(v) => setFilter('managerAccountId', v)}
          />
          <SearchableSelect
            ariaLabel="ITC status"
            placeholder="All ITC statuses"
            clearable
            value={filters.itcStatus ?? ''}
            options={enumOptions(EDITABLE_ITC_STATUSES, LABELS.itcStatus)}
            onChange={(v) => setFilter('itcStatus', v)}
          />
        </div>
        <DataTable<MarketingExpense>
          tableId="marketing"
          rows={list.data?.data}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          meta={list.data?.meta}
          onPageChange={setPage}
          sort={sort}
          onSortChange={setSort}
          onRowClick={canWrite ? (row) => setEditing({ mode: 'edit', row }) : undefined}
          columns={[
            {
              key: 'transactionDate',
              header: 'Date',
              sortKey: 'transactionDate',
              render: (r) => (
                <span className="whitespace-nowrap">{formatDate(r.transactionDate)}</span>
              ),
            },
            {
              key: 'app',
              header: 'App',
              sortKey: 'app',
              render: (r) => (
                <span className="flex items-center gap-2">
                  <AppIcon src={r.app.iconUrl} name={r.app.name} size={22} />
                  {r.app.name}
                </span>
              ),
            },
            {
              key: 'platform',
              header: 'Platform',
              render: (r) => r.platform.name,
            },
            {
              key: 'account',
              header: 'Account',
              sortKey: 'account',
              render: (r) => (
                <div>
                  <p>{r.advertisingAccount.accountName}</p>
                  <p className="text-xs text-slate-400">
                    {r.billingType === 'POSTPAID'
                      ? `Credit · ${r.managerAccount?.name ?? ''}`
                      : 'Prepaid'}
                  </p>
                </div>
              ),
            },
            {
              key: 'expenseBeforeGst',
              header: 'Before GST',
              align: 'right',
              sortKey: 'expenseBeforeGst',
              render: (r) => formatMoney(r.expenseBeforeGstInr),
            },
            {
              key: 'gstRate',
              header: 'Rate',
              align: 'right',
              render: (r) => formatPercent(r.gstRate),
            },
            {
              key: 'gst',
              header: 'GST',
              align: 'right',
              render: (r) => (
                <span
                  className="text-violet-700"
                  title={r.gstAmountOverridden ? 'GST entered from invoice' : undefined}
                >
                  {formatMoney(r.gstAmountInr)}
                  {r.gstAmountOverridden && ' *'}
                </span>
              ),
            },
            {
              key: 'totalAmount',
              header: 'Total',
              align: 'right',
              sortKey: 'totalAmount',
              render: (r) => (
                <span className="font-medium text-slate-900">{formatMoney(r.totalAmountInr)}</span>
              ),
            },
            {
              key: 'itcStatus',
              header: 'ITC',
              sortKey: 'itcStatus',
              render: (r) => <ItcBadge status={r.itcStatus} />,
            },
            {
              key: 'invoice',
              header: 'Invoice',
              render: (r) => r.invoiceNumber ?? <span className="text-slate-300">—</span>,
            },
            {
              key: 'files',
              header: <Paperclip className="size-4" aria-label="Attachments" />,
              render: (r: MarketingExpense) =>
                r.attachmentCount ? (
                  <span
                    className="inline-flex items-center gap-0.5 text-xs text-slate-500"
                    title={`${r.attachmentCount} attachment(s)`}
                  >
                    <Paperclip className="size-3.5" />
                    {r.attachmentCount}
                  </span>
                ) : null,
            },
            ...(canWrite
              ? [
                  {
                    key: '__actions',
                    header: <span className="sr-only">Actions</span>,
                    align: 'right' as const,
                    render: (r: MarketingExpense) => (
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Edit entry"
                          onClick={() => setEditing({ mode: 'edit', row: r })}
                        >
                          <Pencil className="size-4" />
                        </button>
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label="Delete entry"
                          onClick={() => setDeleting(r)}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
      <p className="mt-2 text-xs text-slate-500">* GST amount entered manually from the invoice.</p>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={
          editRow
            ? `Edit entry — ${editRow.advertisingAccount.accountName}, ${formatDate(editRow.transactionDate)}`
            : 'Add marketing entry'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" form="marketing-form">
              {editRow ? 'Save changes' : 'Create'}
            </Button>
          </>
        }
      >
        {editing && (
          <ResourceForm
            key={editRow?.id ?? 'new'}
            formId="marketing-form"
            fields={editRow ? editFields : createFields}
            schema={editRow ? marketingExpenseUpdateSchema : marketingExpenseCreateSchema}
            isEdit={!!editRow}
            lookups={lookups}
            defaultValues={
              editRow
                ? {
                    expenseBeforeGst: String(Number(editRow.expenseBeforeGst)),
                    gstAmount: String(Number(editRow.gstAmount)),
                    invoiceNumber: str(editRow.invoiceNumber),
                    invoiceDate: str(editRow.invoiceDate),
                    vendorName: str(editRow.vendorName),
                    vendorGstin: str(editRow.vendorGstin),
                    itcStatus: (EDITABLE_ITC_STATUSES as readonly string[]).includes(
                      editRow.itcStatus,
                    )
                      ? editRow.itcStatus
                      : '',
                    notes: str(editRow.notes),
                  }
                : {
                    transactionDate: yesterdayInTimezone(),
                    appId: '',
                    advertisingAccountId: '',
                    expenseBeforeGst: '',
                    invoiceNumber: '',
                    invoiceDate: '',
                    vendorName: '',
                    vendorGstin: '',
                    itcStatus: 'ELIGIBLE',
                    notes: '',
                  }
            }
            onSubmit={async (values: FormValues) => {
              if (editRow) {
                const body: FormValues = { ...values, updatedAt: editRow.updatedAt };
                // Only send a GST override when the user actually changed the GST figure.
                if (String(Number(editRow.gstAmount)) === values.gstAmount) delete body.gstAmount;
                await api.put(`/marketing-expenses/${editRow.id}`, body);
                toast.success('Entry updated');
              } else {
                await api.post('/marketing-expenses', values);
                toast.success('Entry created');
              }
              setEditing(null);
              invalidate();
            }}
          />
        )}
        {editRow && (
          <AttachmentsPanel
            entityType="MarketingExpense"
            entityId={editRow.id}
            onChange={invalidate}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this entry?"
        message={
          deleting && (
            <>
              {deleting.advertisingAccount.accountName} on {formatDate(deleting.transactionDate)} (
              {formatMoney(deleting.expenseBeforeGstInr)}). The day will show as missing again until
              re-entered. The deletion is recorded in the audit log.
            </>
          )
        }
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

export function Stat({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: string;
  tone?: 'gst' | 'profit' | 'loss';
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p
        className={
          'num mt-1 text-xl font-semibold ' +
          (tone === 'gst'
            ? 'text-violet-700'
            : tone === 'profit'
              ? 'text-emerald-700'
              : tone === 'loss'
                ? 'text-rose-700'
                : 'text-slate-900')
        }
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}
